import { type TFunction } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import { implementations } from '../RouterDetailsPage/endpoints';
import { type EndpointImplementation } from '../RouterDetailsPage/endpoints/implementations/types';

export type EndpointClass = {
    type: keyof typeof implementations;
    label: string;
    description: string;
    group: string;
    FormComponent: React.FC<{ router: RouterSpecsFragment }>;
    submit: EndpointImplementation<any, any>['submitCreation'];
};

const getAvailableEndpoints = (router: RouterSpecsFragment, t: TFunction) => {
    const results: EndpointClass[] = [];
    let hasApplicationModules = false;
    let hasFinderApplicationPublicModules = false;
    let hasFinderApplicationPrivateModules = false;

    router.company.modules.forEach(module => {
        switch (module.__typename) {
            case 'StandardApplicationModule':
                hasApplicationModules = true;
                results.push({
                    type: 'StandardApplicationEntrypoint',
                    label: `${module.displayName} - Car Listing Entry Point`,
                    description: `${module.displayName} - Car Listing Entry Point Description`,
                    group: 'Application',
                    FormComponent: implementations.StandardApplicationEntrypoint.FormComponent,
                    submit: (apolloClient, routerId, values) =>
                        implementations.StandardApplicationEntrypoint.submitCreation(apolloClient, routerId, {
                            ...values,
                            applicationModuleId: module.id,
                        }),
                });

                results.push({
                    type: 'StandardApplicationPublicAccessEntrypoint',
                    label: `${module.displayName} - Public Access Car Listing Entry Point`,
                    description: `${module.displayName} - Public Access Car Listing Entry Point Description`,
                    group: 'Application',
                    FormComponent: implementations.StandardApplicationPublicAccessEntrypoint.FormComponent,
                    submit: (apolloClient, routerId, values) =>
                        implementations.StandardApplicationPublicAccessEntrypoint.submitCreation(
                            apolloClient,
                            routerId,
                            {
                                ...values,
                                applicationModuleId: module.id,
                            }
                        ),
                });

                break;

            case 'EventApplicationModule':
                hasApplicationModules = true;
                results.push({
                    type: 'EventApplicationEntrypoint',
                    label: `${module.displayName} - Event Entry Point`,
                    description: `${module.displayName} - Event Entry Point Description`,
                    group: 'Application',
                    FormComponent: implementations.EventApplicationEntrypoint.FormComponent,
                    submit: (apolloClient, routerId, values) =>
                        implementations.EventApplicationEntrypoint.submitCreation(apolloClient, routerId, {
                            ...values,
                            eventApplicationModuleId: module.id,
                        }),
                });
                break;

            case 'ConfiguratorModule':
                hasApplicationModules = true;
                results.push({
                    type: 'ConfiguratorApplicationEntrypoint',
                    label: `${module.displayName} - Configurator Entry Point`,
                    description: `${module.displayName} - Configurator Entry Point Description`,
                    group: 'Application',
                    FormComponent: implementations.ConfiguratorApplicationEntrypoint.FormComponent,
                    submit: (apolloClient, routerId, values) =>
                        implementations.ConfiguratorApplicationEntrypoint.submitCreation(apolloClient, routerId, {
                            ...values,
                            configuratorApplicationModuleId: module.id,
                        }),
                });
                break;

            case 'MobilityModule':
                hasApplicationModules = true;
                results.push({
                    type: 'MobilityApplicationEntrypoint',
                    label: `${module.displayName} - Mobility Entry Point`,
                    description: `${module.displayName} - Mobility Entry Point Description`,
                    group: 'Application',
                    FormComponent: implementations.MobilityApplicationEntrypoint.FormComponent,
                    submit: (apolloClient, routerId, values) =>
                        implementations.MobilityApplicationEntrypoint.submitCreation(apolloClient, routerId, {
                            ...values,
                            mobilityApplicationModuleId: module.id,
                        }),
                });
                break;

            case 'WebsiteModule':
                results.push({
                    type: 'WebPageEndpoint',
                    label: `${module.displayName} - Home Page`,
                    description: `${module.displayName} - Home Page Description`,
                    group: 'Application',
                    FormComponent: implementations.WebPageEndpoint.getFormComponent({ moduleId: module.id }),
                    submit: (apolloClient, routerId, values) =>
                        implementations.WebPageEndpoint.submitCreation(apolloClient, routerId, {
                            ...values,
                            websiteModuleId: module.id,
                        }),
                });
                break;

            case 'FinderApplicationPrivateModule':
                hasApplicationModules = true;
                hasFinderApplicationPrivateModules = true;
                break;

            case 'FinderApplicationPublicModule':
                hasApplicationModules = true;
                hasFinderApplicationPublicModules = true;
                break;

            default:
                break;
        }
    });

    if (hasFinderApplicationPublicModules) {
        results.push({
            type: 'FinderApplicationPublicAccessEntrypoint',
            label: `Public Access Finder Entry Point`,
            description: `Public Access Finder Entry Point Description`,
            group: 'Application',
            FormComponent: implementations.FinderApplicationPublicAccessEntrypoint.FormComponent,
            submit: implementations.FinderApplicationPublicAccessEntrypoint.submitCreation,
        });
    }

    if (hasFinderApplicationPrivateModules) {
        results.push({
            type: 'FinderApplicationEntrypoint',
            label: `Private Access Finder Entry Point`,
            description: `Private Access Finder Entry Point Description`,
            group: 'Application',
            FormComponent: implementations.FinderApplicationEntrypoint.FormComponent,
            submit: implementations.FinderApplicationEntrypoint.submitCreation,
        });
    }

    if (hasApplicationModules) {
        results.push({
            type: 'ApplicationListEndpoint',
            label: 'Application List & Details',
            description: 'Provide application listing & details for one or many application modules',
            group: 'Application',
            FormComponent: implementations.ApplicationListEndpoint.FormComponent,
            submit: implementations.ApplicationListEndpoint.submitCreation,
        });

        results.push({
            type: 'CustomerListEndpoint',
            label: 'Customer List & Details',
            description: 'Provide customer listing & details for one or many application modules',
            group: 'Application',
            FormComponent: implementations.CustomerListEndpoint.FormComponent,
            submit: implementations.CustomerListEndpoint.submitCreation,
        });
    }

    results.push({
        type: 'DummyPrivatePageEndpoint',
        label: 'Private Page',
        description: 'Serve a private page with custom body',
        group: 'Dummy',
        FormComponent: implementations.DummyPrivatePageEndpoint.FormComponent,
        submit: implementations.DummyPrivatePageEndpoint.submitCreation,
    });

    results.push({
        type: 'DummyWelcomePageEndpoint',
        label: 'Welcome Page',
        description: 'Serve a welcome page',
        group: 'Dummy',
        FormComponent: implementations.DummyWelcomePageEndpoint.FormComponent,
        submit: implementations.DummyWelcomePageEndpoint.submitCreation,
    });

    return results;
};

export default getAvailableEndpoints;
